import React, { createContext, useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { settings } from 'config';
import { getColor, getItemFromStore } from 'helpers/utils';
import useToggleStyle from 'hooks/useToggleStyle';
import { configReducer } from 'reducers/configReducer';
import { useTranslation } from "react-i18next";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";

export const AppContext = createContext(settings);
export const UpdateLocation = createContext()
const AppProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const configState = {
    isFluid: getItemFromStore('isFluid', settings.isFluid),
    isRTL: getItemFromStore('isRTL', settings.isRTL),
    isDark: getItemFromStore('isDark', settings.isDark),
    theme: getItemFromStore('theme', settings.theme),
    navbarPosition: getItemFromStore('navbarPosition', settings.navbarPosition),
    disabledNavbarPosition: [],
    isNavbarVerticalCollapsed: getItemFromStore(
      'isNavbarVerticalCollapsed',
      settings.isNavbarVerticalCollapsed
    ),
    navbarStyle: getItemFromStore('navbarStyle', settings.navbarStyle),
    currency: settings.currency,
    showBurgerMenu: settings.showBurgerMenu,
    showSettingPanel: false,
    navbarCollapsed: false,
    openAuthModal: false,
    location: getItemFromStore('location', settings.location || null), // Add location state
    language: getItemFromStore('culture', 'nl'),
  };

  const [logoRefreshKey, setLogoRefreshKey] = React.useState(0);
  const refreshLogo = () => setLogoRefreshKey(prev => prev + 1);
  const [isEmployeeView, setIsEmployeeView] = React.useState(
    sessionStorage.getItem("isEmployeeView") === "true"
  );
  const [locations, setLocations] = React.useState([]);
  const [config, configDispatch] = useReducer(configReducer, configState);
  const [isAttendanceEnabled, setIsAttendanceEnabled] = React.useState(false);
  const [notificationsCount, setNotificationsCount] = React.useState(0);

  const ATTENDANCE_API = `${process.env.REACT_APP_API_URL}/attendanceRoute`;
  const authHeaders = { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } };
  const STATUS_TO_PHASE = { working: "working", onBreak: "break", completed: "done" };

  const [attendancePhase, setAttendancePhase] = React.useState("idle"); // idle | working | break | done
  const [attendanceId, setAttendanceId] = React.useState(null);
  const [clockInTime, setClockInTime] = React.useState(null);
  const [workSeconds, setWorkSeconds] = React.useState(0);
  const [breakSeconds, setBreakSeconds] = React.useState(0);
  const [attendanceBusy, setAttendanceBusy] = React.useState(false);
  const [breakTaken, setBreakTaken] = React.useState(false);

  React.useEffect(() => {
    let timer = null;
    if (attendancePhase === "working") {
      timer = setInterval(() => setWorkSeconds(prev => prev + 1), 1000);
    } else if (attendancePhase === "break") {
      timer = setInterval(() => setBreakSeconds(prev => prev + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [attendancePhase]);

  const getLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ locationStatus: "unavailable" });
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            locationStatus: "captured",
          }),
        (err) =>
          resolve({
            locationStatus: err.code === err.PERMISSION_DENIED ? "denied" : "unavailable",
          }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const warnIfLocationMissing = (loc) => {
    if (loc.locationStatus === "captured") return;
    toast.warn(
      t(loc.locationStatus === "denied" ? "locationPermissionDenied" : "locationNotCaptured"),
      {
        position: "top-right",
        autoClose: 3000,
        icon: <i className="fas fa-exclamation-triangle text-warning" />,
        className: "bg-warning-subtle text-warning border-start border-warning border-4",
        progressClassName: "bg-warning",
      }
    );
  };

  const applyAttendance = (attendance) => {
    if (!attendance) {
      setAttendancePhase("idle");
      setAttendanceId(null);
      setClockInTime(null);
      setWorkSeconds(0);
      setBreakSeconds(0);
      setBreakTaken(false);
      return;
    }
    setAttendanceId(attendance.AttendanceId);
    setClockInTime(moment(attendance.ClockInTime).format("HH:mm"));
    setWorkSeconds(attendance.workedSeconds);
    setBreakSeconds(attendance.breakSeconds);
    setBreakTaken(!!attendance.breakTaken);
    setAttendancePhase(STATUS_TO_PHASE[attendance.Status] || "idle");
  };

  const applyCustomerConfig = (customer) => {
     const enabled = Boolean(customer?.IsAttendanceEnabled);
     setIsAttendanceEnabled(enabled);
   };

  const fetchCustomerConfig = async () => {
    const customerId = localStorage.getItem("customerId");
    if (!customerId) return;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
        authHeaders
      );
      applyCustomerConfig(data);
    } catch (error) {
      console.error("Error loading customer config:", error);
    }
  };

  const loadAttendance = async () => {
    const userId = localStorage.getItem("UserId");
    if (!userId) return;
    try {
      const { data } = await axios.get(`${ATTENDANCE_API}/today`, {
        params: { userId, date: moment().format("YYYY-MM-DD") },
        ...authHeaders,
      });
      applyAttendance(data.attendance);
    } catch (error) {
      console.error("Error loading attendance:", error);
    }
  };

  useEffect(() => {
    fetchCustomerConfig();
    loadAttendance();
    // re-sync when the tab becomes active again (browsers throttle timers in background tabs)
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchCustomerConfig();
        loadAttendance();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const handleAttendanceError = (error, fallbackKey) => {
    toast.error(error.response?.data?.message || t(fallbackKey), { position: "top-right" });
    if (error.response?.status === 409) loadAttendance(); // state changed elsewhere, re-sync
  };

  // Runs an attendance API call with busy-state + error handling.
  // Resolves with the response data, or null if the request failed or was aborted.
  const requestAttendance = async (request, errorKey) => {
    setAttendanceBusy(true);
    try {
      return await request();
    } catch (error) {
      handleAttendanceError(error, errorKey);
      return null;
    } finally {
      setAttendanceBusy(false);
    }
  };

  const getRectOrFallback = (el, fallback) => {
    const rect = el?.getBoundingClientRect();
    return rect && rect.width ? rect : fallback;
  };

  const getSlotFallbackRect = () => ({ top: 18, left: window.innerWidth - 270, width: 140, height: 34 });

  // Hides the given elements, plays the flight, then cleans up.
  const flyAndCleanup = async (flyer, from, to, fwd, hiddenEls) => {
    const els = hiddenEls.filter(Boolean);
    els.forEach(el => el.classList.add('invisible'));
    await flyTo(flyer, from, to, fwd, 720);
    flyer.remove();
    els.forEach(el => el.classList.remove('invisible'));
  };

  const flyTo = (flyer, r0, r1, fwd, duration = 750) => {
    const o = easing => ({ duration, easing, fill: 'forwards' });
    const layerRing = flyer.querySelector('.layer-ring');
    const layerPill = flyer.querySelector('.layer-pill');

    const anims = [
      flyer.animate([{ left: `${r0.left}px` }, { left: `${r1.left}px` }], o('cubic-bezier(.6, 0, .25, 1)')),
      flyer.animate([{ top: `${r0.top}px` }, { top: `${r1.top}px` }], o('cubic-bezier(.25, .1, .2, 1)')),
      flyer.animate(
        [
          { width: `${r0.width}px`, height: `${r0.height}px`, borderRadius: fwd ? '50%' : '999px' },
          { width: `${r1.width}px`, height: `${r1.height}px`, borderRadius: fwd ? '999px' : '50%' }
        ],
        o('cubic-bezier(.55, 0, .2, 1)')
      ),
      layerRing.animate(
        fwd ? [{ opacity: 1 }, { opacity: 0, offset: 0.42 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }],
        { duration, fill: 'forwards' }
      ),
      layerPill.animate(
        fwd ? [{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0, offset: 0.42 }, { opacity: 0 }],
        { duration, fill: 'forwards' }
      )
    ];
    return Promise.all(anims.map(a => a.finished.catch(() => {})));
  };

  const createStandardFlyer = (r0, mode, timerText) => {
    const flyer = document.createElement('div');
    flyer.className = 'position-fixed bg-white border shadow-sm overflow-hidden pointer-events-none';
    flyer.style.zIndex = '9999';

    const layerRing = document.createElement('div');
    layerRing.className = 'layer-ring position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center';
    layerRing.innerHTML = mode === 'done'
      ? `<span class="text-success fs-9 lh-1 mb-1">&#10003;</span><span class="text-dark fs-11 fw-semibold lh-1">${timerText}</span><span class="text-success fs-10 text-uppercase lh-1">${t("worked")}</span>`
      : `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F6FE0" stroke-width="2.2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg><span class="text-primary fs-11 fw-semibold lh-1 mt-1">${t("clockIn")}</span>`;

    const layerPill = document.createElement('div');
    layerPill.className = 'layer-pill position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gap-2 px-3 text-nowrap';
    layerPill.innerHTML = `
      <svg width="8" height="8" viewBox="0 0 8 8" class="flex-shrink-0"><circle cx="4" cy="4" r="4" fill="#16a34a"/></svg>
      <span class="text-dark fs-11 fw-semibold">${t("working")}</span>
      <span class="text-400 fs-11">&middot;</span>
      <span class="text-dark fs-11 fw-semibold">${timerText}</span>
    `;

    flyer.append(layerRing, layerPill);
    document.body.appendChild(flyer);
    return flyer;
  };

   const handleClockIn = async () => {
     const ring = document.getElementById('dashboard-clockin-ring');
     const slot = document.getElementById('header-attendance-slot');
     if (attendanceBusy) return;

         const data = await requestAttendance(async () =>{     
       const userId = localStorage.getItem("UserId");
       const customerId = localStorage.getItem("customerId");
       if (!userId || !customerId) {
         toast.error(t("sessionExpired"), { position: "top-right" });
         return null;
       }
       const loc = await getLocation(); // triggers the browser permission prompt
       warnIfLocationMissing(loc);
 
       const res = await axios.post(
         `${ATTENDANCE_API}/clockIn`,
         { userId, customerId, attendanceDate: moment().format("YYYY-MM-DD"), ...loc },
         authHeaders
       );
       return res.data;
     }, "clockInError");
     if (!data) return;
 
     applyAttendance(data);
 
     if (ring) {
       const r0 = ring.getBoundingClientRect();
       const flyer = createStandardFlyer(r0, 'idle', '00:00:00');
 
       requestAnimationFrame(() => {
         const r1 = getRectOrFallback(slot, getSlotFallbackRect());
         flyAndCleanup(flyer, r0, r1, true, [slot]);
       });
     }
   };

  const handleClockOut = async () => {
    if (attendanceBusy || !attendanceId) return;

        const data = await requestAttendance(async () => {
      const loc = await getLocation();
      warnIfLocationMissing(loc);
      const res = await axios.patch(
        `${ATTENDANCE_API}/clockOut`,
        { userId: localStorage.getItem("UserId"), attendanceId, ...loc },
        authHeaders
      );
      return res.data;
    }, "clockOutError");
    if (!data) return;

    const slot = document.getElementById('header-attendance-slot');
    const r0 = getRectOrFallback(slot, getSlotFallbackRect());

    if (slot) slot.classList.add('invisible');

    const flyer = createStandardFlyer(r0, 'done', formatSecondsToHM(data.workedSeconds));

    applyAttendance(data);

    requestAnimationFrame(() => {
      const ring = document.getElementById('dashboard-clockin-ring');
      const r1 = getRectOrFallback(ring, { top: 220, left: window.innerWidth - 300, width: 110, height: 110 });
      flyAndCleanup(flyer, r0, r1, false, [ring, slot]);
    });
  };

  const runBreakTransition = async (endpoint) => {
    if (attendanceBusy || !attendanceId) return;
    const data = await requestAttendance(async () => {
      const res = await axios.patch(
        `${ATTENDANCE_API}/${endpoint}`,
        { userId: localStorage.getItem("UserId"), attendanceId },
        authHeaders
      );
      return res.data;
    }, "attendanceError");
    if (data) applyAttendance(data);
  };

  const handleBreak = () => runBreakTransition("breakStart");
  const handleResume = () => runBreakTransition("breakEnd");
  const formatSecondsToHMS = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const formatSecondsToHM = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}${t("hourShort", "h")} ${String(m).padStart(2, "0")}${t("minuteShort", "m")}`;
  };

  const setConfig = (key, value) => {
    configDispatch({
      type: 'SET_CONFIG',
      payload: {
        key,
        value,
        setInStore: ['isFluid', 'isRTL', 'isDark', 'theme', 'navbarPosition', 'location', 'language'].includes(key)
      }
    });
  };

  const changeTheme = theme => {
    const isDark =
      theme === 'auto'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : theme === 'dark';

    document.documentElement.setAttribute(
      'data-bs-theme',
      isDark ? 'dark' : 'light'
    );

    setConfig('theme', theme);
    setConfig('isDark', isDark);
  };

  const changeLocation = location => {
    setConfig('location', location);
  };

  const { isLoaded } = useToggleStyle(config.isRTL, config.isDark);

  useEffect(() => {
    const isDark =
      config.theme === 'auto'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : config.theme === 'dark';

    setConfig('isDark', isDark);
  }, [config.theme]);

  useEffect(() => {
    if (config.language && i18n.language !== config.language) {
      i18n.changeLanguage(config.language);
      localStorage.setItem('culture', config.language);
    }
  }, [config.language]);

  const getThemeColor = name => getColor(name);

  if (!isLoaded) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: config.isDark
            ? getThemeColor('dark')
            : getThemeColor('light')
        }}
      />
    );
  }

  return (
    <AppContext.Provider
      value={{
        config, setConfig, configDispatch, changeTheme, changeLocation, getThemeColor, logoRefreshKey, refreshLogo, locations, setLocations, notificationsCount, setNotificationsCount,
        isEmployeeView,
        setIsEmployeeView,
        isAttendanceEnabled,
        attendancePhase,
        setAttendancePhase,
        attendanceId,
        attendanceBusy,
        loadAttendance,
        breakTaken,
        clockInTime,
        workSeconds,
        breakSeconds,
        handleClockIn,
        handleBreak,
        handleResume,
        handleClockOut,
        formatSecondsToHMS,
        formatSecondsToHM,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = { children: PropTypes.node };

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
